import { useEffect, useContext, useState } from 'react';
import { Box, Card, CardContent, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { EditOutlined, DeleteOutlineOutlined, DashboardOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import MaterialTable from '@material-table/core';
import { UserContext } from '../../contexts/UserContext';
import { generateClient } from 'aws-amplify/api';
import { enqueueSnackbar } from 'notistack';
import { deleteManualProfile, createAuditLog, updateTenantSettings } from '../../graphql/mutations';
import { manualProfileByTenant } from '../../graphql/queries';
import { PageAppBar } from '../../components/pageAppBar';
import { ConfirmDialog } from '../../components/confirmDialog/confirmDialog';
import { cardStyle } from '../../theme';
import ManualDialDashboard from '../../components/engagement/manualDialDashboard';
import { fetchAllPages } from '../../tools/graphqlPaging';

export function ManualProfiles() {
	const client = generateClient();
	const userContext = useContext(UserContext);
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const [profiles, setProfiles] = useState([]);
	const [toDelete, setToDelete] = useState(null);
	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

	const [showDashboard, setShowDashboard] = useState(false);
	const [showSkillId, setShowSkillId] = useState(null);
	const [showProfileName, setShowProfileName] = useState('');
	const [manualStrategy, setManualStrategy] = useState(userContext.manualStrategy || 'profileSelect');

	useEffect(() => {
		async function getData() {
			// Retrieving profiles
			setLoading(true);
			await getProfiles();
			setLoading(false);
		}
		if (userContext.tenantId) {
			getData();
			setManualStrategy(userContext.manualStrategy || 'profileSelect');
		}
	}, [userContext.tenantId]);

	async function getProfiles() {
		// Get Profiles - BY TENANT. Paged so all profiles are returned.
		const allProfiles = await fetchAllPages({
			client,
			query: manualProfileByTenant,
			resultKey: 'manualProfileByTenant',
			variables: { tenant: userContext.tenantId },
		});
		setProfiles(
			allProfiles.sort(
				(a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt),
			),
		);
	}

	async function handleStrategyChange(newStrategy) {
		try {
			setManualStrategy(newStrategy);
			await client.graphql({
				query: updateTenantSettings,
				variables: {
					input: {
						id: userContext.tenantId,
						manualStrategy: newStrategy,
					},
				},
			});
			enqueueSnackbar('Manual dialing strategy updated.', {
				variant: 'success',
				autoHideDuration: 2000,
			});
		} catch (e) {
			console.error(e);
			enqueueSnackbar('Error updating manual dialing strategy.', {
				variant: 'error',
				autoHideDuration: 2000,
			});
		}
	}

	async function handleDelete() {
		try {
			setLoading(true);

			await client.graphql({
				query: deleteManualProfile,
				variables: { input: { id: toDelete } },
			});
			await client.graphql({
				query: createAuditLog,
				variables: {
					input: {
						tenant: userContext.tenantId,
						type: 'ManualProfile',
						timestamp: new Date().toISOString(),
						userName: userContext.email,
						documentId: toDelete,
						name: profiles.find(profile => profile.id === toDelete).name,
						currentValue: "{}",
						action: 'DELETED',
						ttl: Math.floor(Date.now() / 1000) + (6 * 30 * 24 * 60 * 60), //6 months
					}
				}
			});
			setToDelete(null);
			setConfirmDeleteOpen(false);
			enqueueSnackbar('Profile deleted successfully.', {
				variant: 'success',
				autoHideDuration: 2000
			});
			await getProfiles();
			setLoading(false);

		} catch (e) {
			console.error(e);
			enqueueSnackbar('Error deleting profile.', {
				variant: 'error',
				autoHideDuration: 2000
			});
			setLoading(false);
			setToDelete(null);
			setConfirmDeleteOpen(false);
		}
	}

	function handleCancelDelete() {
		setToDelete(null);
		setConfirmDeleteOpen(false);
	}

	return (
		<Box>
			<PageAppBar
				title="Manual Dialing Profiles"
				// description="Set up Call Windows, Cadence, Attempts, and More"
				actionOneText={userContext.securityOptions.communicationProfiles > 1 && !showDashboard ? "+ Profile" : !showDashboard ? "" : "Back"}
				actionOneHandler={() => {
					if (showDashboard) {
						setShowDashboard(false);
						return;
					} else {
						navigate('/manual-profiles/new');
					}
				}}
				actionTwoText={<>{showDashboard ? "Back to Profiles" : "All Manual Calls"}</>}
				actionTwoHandler={() => {
					if (showDashboard) {
						setShowDashboard(false);
						return;
					} else {
						setShowDashboard(true);
						setShowSkillId(null);
						setShowProfileName('');
					}
				}}
				actionOneDisabled={userContext.securityOptions.communicationProfiles < 2}
			/>
			{!showDashboard && <Box display="flex" flexDirection="column" gap={2}>
				<Card variant='outlined' sx={cardStyle}>
					<CardContent>
						<Typography variant="h6" gutterBottom>Profile Selection Strategy</Typography>
						<Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
							Choose how agents are assigned a manual dialing profile.
						</Typography>
						<FormControl sx={{ minWidth: 300 }}>
							<InputLabel>Strategy</InputLabel>
							<Select
								label="Strategy"
								value={manualStrategy}
								onChange={(e) => handleStrategyChange(e.target.value)}
							>
								<MenuItem value="profileSelect">
									Manual Profile Select
								</MenuItem>
								<MenuItem value="agentLastCall">
									Agent Last Call
								</MenuItem>
							</Select>
						</FormControl>
						<Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
							{manualStrategy === 'profileSelect'
								? 'Agents manually choose which dialing profile to use.'
								: 'The profile is automatically selected based on the skill from the agent\'s last call.'}
						</Typography>
					</CardContent>
				</Card>
				<Card variant='outlined' sx={cardStyle}>
					<CardContent>
						<MaterialTable
							components={{
								Container: (props) => <Box {...props} elevation={0} />,
							}}
							title=""
							data={profiles}
							columns={[
								{ title: 'Name', field: 'name' },
								{
									title: 'Contact Source', field: 'manualStrategy',
									render: (rowData) => rowData.manualStrategy === 'agentLastCall' ? 'Last Contact Dialed' : 'Segment Search'
								},
							]}
							isLoading={loading}
							options={{
								actionsColumnIndex: -1,
								pageSize: 10,
								searchFieldStyle: {
									marginBottom: '16px',
									marginleft: '-28px',
								},
								headerStyle: {
									borderTop: '1px solid #e0e0e0',
									padding: '16px',
								},
								searchFieldVariant: 'outlined',
								paginationType: 'stepped',
							}}
							actions={[
								{
									icon: () => <DashboardOutlined color="primary" />,
									tooltip: 'View Dashboard',
									onClick: (event, rowData) => {
										setShowDashboard(true);
										setShowSkillId(rowData.skills);
										setShowProfileName(rowData.name);
									},
								},
								{
									disabled: userContext.securityOptions.communicationProfiles < 2,
									icon: () => <EditOutlined color="primary" />,
									tooltip: 'Edit Profile',
									onClick: (event, rowData) => {
										navigate(`/manual-profiles/${rowData.id}`);
									},
								},
								rowData => function () {
									const disabled = userContext.securityOptions.communicationProfiles < 2;
									return {
										icon: () => <DeleteOutlineOutlined color={disabled ? 'disabled' : 'primary'} />,
										tooltip: 'Delete Profile',
										onClick: (event, rowData) => {
											setToDelete(rowData.id);
											setConfirmDeleteOpen(true);
										},
										disabled
									}
								}(),
							]}
							onRowClick={(event, rowData) => {
								navigate(`/manual-profiles/${rowData.id}`);
							}}
						/>
					</CardContent>
				</Card>
			</Box>}
			{showDashboard &&
				<ManualDialDashboard
					skillId={showSkillId}
					profileName={showProfileName}
					setShowDashboard={setShowDashboard}
				/>
			}
			<ConfirmDialog
				open={confirmDeleteOpen}
				title="Delete Profile"
				description="Are you sure you want to delete this profile?"
				actionOneText="Delete"
				actionOneHandler={() => {
					handleDelete();
				}}
				actionTwoText="Cancel"
				actionTwoHandler={() => {
					handleCancelDelete();
				}}
			/>
		</Box >
	);
}
